import React, { useEffect, useState } from 'react';
import { marketingService } from '../../_services/marketing.service';
import { formatPence, creditsToPence } from '../pricing/money';

/**
 * Settings > Marketing, for an owner with more than one location: how
 * marketing credits work across the business.
 *   - every location gets its own free monthly allowance (it pays its own plan);
 *   - credits you BUY go into one balance all your locations share;
 *   - a location can keep its own balance instead ("Separate marketing credits").
 * Renders nothing for a single-site salon or anyone who isn't the owner.
 */
const card = { background: '#fff', border: '1px solid #ECECF0', borderRadius: '14px', padding: '18px 24px', marginBottom: '16px' };
// Pounds, never credits: pence fields when the API sends them, else credits × 2p.
const gbp = (pence, credits) => formatPence(pence != null ? pence : creditsToPence(credits));

function errorText(e) {
    const d = e && e.response && e.response.data;
    return (d && ((d.error && (d.error.responseMessage || d.error.message)) || d.message)) || 'That didn’t save just now. Please try again.';
}

function Switch({ on, disabled, onClick, label }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={!!on}
            aria-label={label}
            onClick={onClick}
            disabled={disabled}
            style={{ width: 44, height: 26, borderRadius: 100, border: 'none', cursor: disabled ? 'default' : 'pointer', padding: 2, flexShrink: 0, background: on ? '#007EA0' : '#DEDEE2', transition: 'background 0.15s', opacity: disabled ? 0.6 : 1 }}
        >
            <span style={{ display: 'block', width: 22, height: 22, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'transform 0.15s', transform: on ? 'translateX(18px)' : 'translateX(0)' }} />
        </button>
    );
}

export default function LocationCreditsCard() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        marketingService.smsWalletLocations()
            .then((res) => { if (alive) setData((res && res.data && res.data.responseData) || null); })
            .catch(() => { if (alive) setData(null); });
        return () => { alive = false; };
    }, []);

    if (!data || !data.multi || !Array.isArray(data.sites)) return null;

    const sharing = data.sites.filter((s) => s.sharesPot);
    const names = sharing.map((s) => s.salonName).filter(Boolean);
    const sharedLine = names.length > 1
        ? `Shared by ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}.`
        : names.length === 1 ? `Used by ${names[0]}.` : 'No location is sharing it at the moment.';

    const toggle = (site) => {
        if (busy || !site.canChange) return;
        setBusy(String(site.salonId));
        setError('');
        marketingService.smsWalletSetSeparate(site.salonId, !site.separate)
            .then((res) => { const next = res && res.data && res.data.responseData; if (next && next.sites) setData(next); })
            .catch((e) => setError(errorText(e)))
            .then(() => setBusy(null));
    };

    return (
        <div style={card}>
            <h5 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 700, color: '#1A1A1E' }}>Message balance across your locations</h5>
            <p style={{ fontSize: '12.5px', color: '#8E8E93', lineHeight: 1.55, margin: '0 0 14px', maxWidth: '640px' }}>
                Every location gets its own free allowance each month. Top-ups go into one balance that all your locations share, so you can top up once and send from anywhere. Turn on Separate balance to give a location its own instead. If you turn it off again, anything left in that location’s own balance stays with it and is used first when it sends.
            </p>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', padding: '12px 14px', background: '#F7F7F9', borderRadius: '10px', marginBottom: '14px' }}>
                <span style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1E' }}>{gbp(data.sharedBalancePence, data.sharedBalance)}</span>
                <span style={{ fontSize: '12.5px', color: '#6B6B73' }}>Shared balance. {sharedLine}</span>
            </div>

            <div>
                {data.sites.map((s) => {
                    const a = s.freeAllowance || {};
                    const own = Number(s.ownBalance) || 0;
                    const ownLine = s.separate
                        ? `Own balance: ${gbp(s.ownBalancePence, own)}`
                        : own > 0 || Number(s.ownBalancePence) > 0 ? `${gbp(s.ownBalancePence, own)} kept for this location, used first here` : '';
                    return (
                        <div key={String(s.salonId)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', padding: '12px 0', borderTop: '1px solid #F0F0F3' }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1E' }}>
                                    {s.salonName || 'Location'}{s.isCurrent ? <span style={{ fontSize: '11px', fontWeight: 600, color: '#6B6B73', marginLeft: 8 }}>This location</span> : null}
                                </div>
                                <div style={{ fontSize: '12.5px', color: '#6B6B73', marginTop: 2 }}>
                                    Free this month: {gbp(a.remainingPence, a.remaining)} of {gbp(a.grantPence, a.grant)}
                                </div>
                                {ownLine ? <div style={{ fontSize: '12.5px', color: '#6B6B73', marginTop: 2 }}>{ownLine}</div> : null}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                                <span style={{ fontSize: '12.5px', color: '#4B4B52' }}>Separate balance</span>
                                <Switch
                                    on={s.separate}
                                    disabled={!s.canChange || !!busy}
                                    onClick={() => toggle(s)}
                                    label={`Separate balance for ${s.salonName || 'this location'}`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            {busy ? <p style={{ fontSize: '12px', fontWeight: 600, color: '#8E8E93', margin: '8px 0 0' }}>Saving…</p> : null}
            {error ? <p style={{ fontSize: '12px', fontWeight: 600, color: '#B14438', margin: '8px 0 0' }}>{error}</p> : null}
        </div>
    );
}
